import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Mail, Send, Trash2, UserPlus, X, Copy, RefreshCw } from "lucide-react";
import {
  listCities,
  listCityInvites,
  listCityEditors,
  inviteCityEditor,
  revokeCityInvite,
  removeCityEditor,
  resendCityInvite,
} from "@/lib/city-teams.functions";

function inviteUrl(token: string) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/city-invite/${token}`;
}

async function copyToClipboard(text: string) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch { return false; }
}

export const Route = createFileRoute("/_authenticated/admin/city-teams")({
  head: () => ({ meta: [
    { title: "City Teams — Admin — What Finland" },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
  component: AdminCityTeams,
});

function AdminCityTeams() {
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ cityId: "", email: "", recipientName: "" });

  const citiesQ = useQuery({ queryKey: ["admin","cities"], queryFn: () => listCities() });
  const invitesQ = useQuery({ queryKey: ["admin","city-invites"], queryFn: () => listCityInvites() });
  const editorsQ = useQuery({ queryKey: ["admin","city-editors"], queryFn: () => listCityEditors() });

  const invMut = useMutation({
    mutationFn: () => inviteCityEditor({ data: { cityId: form.cityId, email: form.email, recipientName: form.recipientName || undefined } }),
    onSuccess: async (row: unknown) => {
      const token = (row as { token?: string } | null)?.token;
      if (token) {
        const link = inviteUrl(token);
        const copied = await copyToClipboard(link);
        toast.success(copied ? "Invitation created — link copied to clipboard." : `Invitation created. Link: ${link}`);
      } else {
        toast.success("Invitation created — email is on the way.");
      }
      setShowInvite(false); setForm({ cityId: "", email: "", recipientName: "" });
      qc.invalidateQueries({ queryKey: ["admin","city-invites"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeCityInvite({ data: { id } }),
    onSuccess: () => { toast.success("Invitation revoked."); qc.invalidateQueries({ queryKey: ["admin","city-invites"] }); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });
  const resendMut = useMutation({
    mutationFn: (id: string) => resendCityInvite({ data: { id } }),
    onSuccess: async (r: unknown) => {
      const res = r as { ok: boolean; link?: string; error?: string };
      if (res.ok) toast.success("Invitation email resent.");
      else if (res.link) {
        await copyToClipboard(res.link);
        toast.warning(`Email not sent (${res.error}). Link copied to clipboard.`);
      } else toast.error(res.error || "Could not resend.");
      qc.invalidateQueries({ queryKey: ["admin","city-invites"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => removeCityEditor({ data: { id } }),
    onSuccess: () => { toast.success("Editor removed."); qc.invalidateQueries({ queryKey: ["admin","city-editors"] }); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <UserPlus className="h-5 w-5 text-slate-900" />
          <h1 className="text-lg font-semibold text-slate-900">City Teams</h1>
          <button onClick={() => setShowInvite(true)}
            className="ml-auto inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-slate-800">
            <Mail className="h-4 w-4" /> Invite City Editor
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">Invitations</h2>
          {invitesQ.isLoading ? <Loader2 className="h-5 w-5 animate-spin text-slate-500" /> :
           !invitesQ.data?.length ? <p className="text-sm text-slate-500">No invitations yet.</p> : (
            <div className="-mx-6 px-6 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="text-xs text-slate-500 uppercase text-left">
                <tr><th className="py-2">City</th><th>Email</th><th>Status</th><th>Sent</th><th>Expires</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {invitesQ.data.map((inv: any) => (
                  <tr key={inv.id}>
                    <td className="py-2 whitespace-nowrap">{inv.cities?.name ?? "—"}</td>
                    <td className="whitespace-nowrap">{inv.email}</td>
                    <td><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded whitespace-nowrap ${
                      inv.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                      inv.status === "revoked" ? "bg-slate-100 text-slate-500" :
                      inv.status === "expired" ? "bg-rose-100 text-rose-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>{inv.status}</span></td>
                    <td className="text-xs text-slate-500 whitespace-nowrap">{inv.invited_email_sent_at ? new Date(inv.invited_email_sent_at).toLocaleDateString() : "pending"}</td>
                    <td className="text-xs text-slate-500 whitespace-nowrap">{new Date(inv.expires_at).toLocaleDateString()}</td>
                    <td className="text-right whitespace-nowrap">
                      {inv.status === "pending" && (
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={async () => {
                              const ok = await copyToClipboard(inviteUrl(inv.token));
                              if (ok) toast.success("Invite link copied."); else toast.error("Could not copy link.");
                            }}
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                            title="Copy invite link"
                          >
                            <Copy className="h-3.5 w-3.5" /> Copy
                          </button>
                          <button
                            disabled={resendMut.isPending}
                            onClick={() => resendMut.mutate(inv.id)}
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-40"
                            title="Resend email"
                          >
                            <RefreshCw className={`h-3.5 w-3.5 ${resendMut.isPending && resendMut.variables === inv.id ? "animate-spin" : ""}`} /> Resend
                          </button>
                          <button onClick={() => revokeMut.mutate(inv.id)} className="text-xs text-rose-600 hover:text-rose-800">Revoke</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

        </section>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">Active Editors</h2>
          {editorsQ.isLoading ? <Loader2 className="h-5 w-5 animate-spin text-slate-500" /> :
           !editorsQ.data?.length ? <p className="text-sm text-slate-500">No active editors.</p> : (
            <ul className="divide-y divide-slate-100">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {editorsQ.data.map((e: any) => (
                <li key={e.id} className="py-3 flex items-center gap-3">
                  <span className="text-sm text-slate-900 flex-1">{e.email ?? e.user_id}</span>
                  <span className="text-xs text-slate-500">{e.cities?.name ?? "—"}</span>
                  <button onClick={() => removeMut.mutate(e.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {showInvite && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setShowInvite(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Invite City Editor</h3>
              <button onClick={() => setShowInvite(false)} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">City</label>
                <select value={form.cityId} onChange={(e) => setForm({ ...form, cityId: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                  <option value="">Select a city…</option>
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {(citiesQ.data ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Recipient Name (Optional)</label>
                <input value={form.recipientName} onChange={(e) => setForm({ ...form, recipientName: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
              </div>
              <button disabled={!form.cityId || !form.email || invMut.isPending} onClick={() => invMut.mutate()}
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-slate-800 disabled:opacity-40">
                {invMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
